/* eslint-disable react/require-default-props */
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { GenericPresetTemplates } from '@src/types.ts/general';
import { observer } from 'mobx-react-lite';
import React, { forwardRef, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { delay } from '../../utility/general';

import type { Template } from '../textProcessor/TextBrandingService';
import type { Dispatch, SetStateAction } from 'react';

type Props = {
  type: 'basic' | 'karaoke';
  addNewTemplate: boolean;
  toggleAddNewTemplate: Dispatch<SetStateAction<boolean>>;
  setTemplateName: Dispatch<SetStateAction<string | undefined>>;
  // brandingTemplates?: Template[];
  // setBrandingTemplates?: Dispatch<SetStateAction<Template[]>>;
  // selectedTemplate?: Template | undefined;
  // setSelectedTemplate?: Dispatch<SetStateAction<Template | undefined>>;
  templateNameError: boolean;
  setTemplateNameError: Dispatch<SetStateAction<boolean>>;
  allowCustomTemplateSave?: boolean;
  hasActiveElement?: boolean;
  hasStickyBottom?: boolean;
  shouldUseTemplatePosition?: boolean;
  onAddNewClick?: () => void;
  /**
   * Fires when the user clicks the inline "Update" button on the active
   * template row (shown only when the current styling has unsaved edits
   * and the row isn't a built-in preset). The host performs the actual
   * `textBrandingService.save(..., 'update')` since it owns the `setting`
   * context. When omitted, no Update button renders.
   */
  onUpdateTemplate?: (template: Template) => void;
  labelText?: string;
  brandingTemplatesFilterFn?: (templates: Template[]) => Template[];
  renderFunction?: (props: {
    handleAddTemplateButtonClick: () => void;
    templates: Template[];
  }) => React.ReactElement;
};

const TextBrandingTemplates = observer(
  forwardRef((props: Props, _ref) => {
    const videoCreator = useVideoCreatorStore();
    const {
      type,
      addNewTemplate,
      toggleAddNewTemplate,
      setTemplateName,
      templateNameError,
      setTemplateNameError,
      allowCustomTemplateSave,
      onAddNewClick,
      onUpdateTemplate,
      brandingTemplatesFilterFn = t => t,
      renderFunction,
    } = props;
    const aspectRatio =
      videoCreator.currentVideo?.aspectRatio ||
      videoCreator.getDefaultAspectRatio();
    const { selectedBrandingTemplate, brandingTemplates, isLoading } =
      videoCreator.textBrandingService;
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      videoCreator.textBrandingService.isTemplateModified = false;
    }, []);

    useEffect(() => {
      if (videoCreator.textBrandingService.closeActiveState) {
        props.toggleAddNewTemplate(false);
      }
    }, [videoCreator.textBrandingService.closeActiveState]);

    useEffect(() => {
      if (brandingTemplates) {
        (async () => {
          if (videoCreator.textBrandingService.isPropsChanged) {
            videoCreator.textBrandingService.selectBrandingTemplate(
              type,
              GenericPresetTemplates.custom,
            );

            await delay(500);
            videoCreator.textBrandingService.isPropsChanged = false;
          }
        })();
      }
    }, [
      selectedBrandingTemplate,
      videoCreator.textBrandingService.isPropsChanged,
    ]);

    useEffect(() => {
      if (addNewTemplate || allowCustomTemplateSave) {
        inputRef.current?.focus();
        inputRef.current?.scrollIntoView();
      }
    }, [allowCustomTemplateSave, addNewTemplate]);

    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (isLoading && type === 'karaoke') {
        contentRef.current?.scrollIntoView();
      }
    }, [isLoading]);

    useEffect(() => {
      if (allowCustomTemplateSave) return;
      videoCreator.textBrandingService.getAllBrandingTemplates(
        type,
        aspectRatio,
        props.hasActiveElement,
      );
    }, [type, allowCustomTemplateSave]);

    const renderDropdownItem = (template: Template) => {
      const isActive = selectedBrandingTemplate?.id === template.id;
      // Inline "Update" affordance: only on the active row, only when the
      // current styling has unsaved edits, and never on the built-in
      // presets (Arbor Default / Custom can't be overwritten).
      const canUpdate =
        isActive &&
        !!onUpdateTemplate &&
        videoCreator.textBrandingService.isTemplateModified &&
        !videoCreator.textBrandingService.isPresetTemplate(
          template.id as string,
        );
      return (
        <TemplateRow
          key={template.id as string}
          isActive={isActive}
          onClick={() => {
            videoCreator.textBrandingService.applyTemplate(
              template,
              type,
              null,
              undefined,
              false,
            );
          }}
        >
          {/* Name in the template's typeface (Font-tab parity) but forced to
              a readable weight + no caps so the list stays scannable. */}
          <TemplateRowName fontFamily={template.fontFamily as string}>
            {template.title}
          </TemplateRowName>
          {/* AaBbCc preview shows the template's font / weight / caps in a
              uniform muted gray (matches the Font tab) — the fill colour is
              intentionally NOT applied so every row's preview reads the same
              shade. */}
          <TemplateRowPreview
            fontFamily={template.fontFamily as string}
            fontWeight={template.fontWeight as number}
            uppercase={template.uppercase as boolean}
          >
            AaBbCc
          </TemplateRowPreview>
          {canUpdate && (
            <TemplateUpdateButton
              type="button"
              onClick={e => {
                e.stopPropagation();
                onUpdateTemplate?.(template);
              }}
            >
              Update
            </TemplateUpdateButton>
          )}
        </TemplateRow>
      );
    };

    const handleAddTemplateButtonClick = () => {
      // When the host wires `onAddNewClick` (Graphics → Text opens a naming
      // modal), delegate entirely — no inline name-input mode on this surface.
      if (onAddNewClick) {
        onAddNewClick();
        return;
      }
      // Legacy inline flow for consumers that don't supply a modal.
      videoCreator.textBrandingService.isTemplateModified = false;
      videoCreator.textBrandingService.prepareTemplateToSave = true;
      toggleAddNewTemplate(true);
      inputRef.current?.focus();
    };

    if (typeof renderFunction === 'function') {
      return renderFunction({
        handleAddTemplateButtonClick,
        templates: brandingTemplatesFilterFn(brandingTemplates),
      });
    }

    // When the user is naming a new template (or saving a one-off custom
    // template), swap the list for a labelled name input. Otherwise show
    // the always-visible template list — "+ Add New Template" on top, then
    // the rows (mirrors the Font tab's list-style layout).
    const isNameInputMode = addNewTemplate || allowCustomTemplateSave;

    return (
      <Main ref={contentRef}>
        {isNameInputMode ? (
          <>
            <FieldName type={type}>Template Name</FieldName>
            <InputContainer error={templateNameError}>
              <InputContent
                ref={inputRef}
                placeholder="Enter Template Name"
                onChange={e => {
                  setTemplateName(e.target.value);
                  setTemplateNameError(false);
                }}
              />
            </InputContainer>
            {templateNameError && (
              <TemplateNameError>Template name is required</TemplateNameError>
            )}
          </>
        ) : (
          <>
            <AddTemplateButton
              type="button"
              onClick={handleAddTemplateButtonClick}
            >
              + Add New Template
            </AddTemplateButton>
            <TemplateList>
              {brandingTemplatesFilterFn(brandingTemplates)?.map(t =>
                renderDropdownItem(t),
              )}
            </TemplateList>
          </>
        )}
      </Main>
    );
  }),
);

export default TextBrandingTemplates;

const Main = styled.div`
  position: relative;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
const InputContainer = styled.div<{ error: boolean }>`
  border: 1px solid
    ${props => (props.error ? 'var(--destructive)' : 'var(--border)')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  cursor: pointer;

  padding: 0px 16px;
  padding-left: 0;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;

  /*
   * Bordered-field-primitive hover/focus standard — see
   * docs/design-system.md "Form Fields (Bordered Primitives)".
   * Single channel: border-color on hover + focus-within, no halo.
   * Error variant keeps the destructive border throughout.
   */
  transition: border-color var(--transition-fast);
  &:hover {
    border-color: ${props =>
      props.error ? 'var(--destructive)' : 'var(--text-muted)'};
  }
  &:focus-within {
    border-color: ${props =>
      props.error ? 'var(--destructive)' : 'var(--accent)'};
  }
`;

const InputContent = styled.input`
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  height: 100%;
  width: 100%;
  background-color: transparent;
  color: var(--text-secondary);
  outline: 0;
  border: 0;
  padding-left: 16px;
  &::placeholder {
    color: var(--border);
  }
`;

/** Always-visible template list — mirrors the Font tab's `SFontList`
 * (scrollable column, thin scrollbar) instead of an absolutely-positioned
 * click-to-open dropdown. */
const TemplateList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 280px;
  overflow-y: auto;
  scrollbar-color: var(--gray-400) transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 2px;
  }
`;

/** One template row — same visual language as the Font tab's `SFontRow`:
 * faint accent tint when active, surface-elevated hover. */
const TemplateRow = styled.div<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 8px;
  border-radius: 6px;
  cursor: pointer;
  background: ${props =>
    props.isActive
      ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
      : 'transparent'};
  &:hover {
    background: ${props =>
      props.isActive
        ? 'color-mix(in srgb, var(--accent) 18%, transparent)'
        : 'var(--surface-elevated)'};
  }
`;

/** Template name in the template's typeface (Font-tab parity) but forced to
 * a readable weight and no caps so the list stays scannable. */
const TemplateRowName = styled.span<{ fontFamily?: string }>`
  flex: 0 1 auto;
  min-width: 0;
  font-family: ${props => (props.fontFamily ? `${props.fontFamily}, ` : '')}
    var(--font-primary);
  font-weight: 400;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  text-transform: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** "AaBbCc" preview carrying the template's full style (font, weight, caps,
 * fill colour) — the style preview moved off the name and onto this chip. */
const TemplateRowPreview = styled.span<{
  fontFamily?: string;
  fontWeight?: number;
  uppercase?: boolean;
}>`
  flex-shrink: 0;
  font-family: ${props => (props.fontFamily ? `${props.fontFamily}, ` : '')}
    var(--font-primary);
  font-size: var(--text-xs);
  font-weight: ${props => props.fontWeight || 400};
  text-transform: ${props => (props.uppercase ? 'uppercase' : 'none')};
  color: var(--text-muted);
`;

/** Small inline "Update" action — right-aligned on the active template row,
 * shown only when there are unsaved style edits. Green (--primary) per the
 * design-system action colour; sized down to sit inside the 36 px row. */
const TemplateUpdateButton = styled.button`
  margin-left: auto;
  flex-shrink: 0;
  height: 24px;
  padding: 0 10px;
  border: none;
  border-radius: var(--radius-button);
  background: var(--primary);
  color: var(--primary-foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  cursor: pointer;
  white-space: nowrap;
  transition: background 120ms ease;
  &:hover {
    background: var(--primary-hover);
  }
  &:active {
    background: var(--primary-pressed);
  }
`;

/** "+ Add New Template" — design-system **secondary** (outline) button,
 * matching `AddSpeakerButton` in CreatorStudioSpeakersPanel: `sm` size,
 * 1 px border, transparent fill, foreground text, surface-elevated +
 * text-muted border on hover. Opens the naming modal via `onAddNewClick`. */
const AddTemplateButton = styled.button`
  align-self: flex-start;
  flex-shrink: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 6px;
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  margin-bottom: 8px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  white-space: nowrap;
  transition: all 150ms ease-out;
  &:hover {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }
  &:active {
    background-color: var(--gray-700);
    scale: 0.98;
  }
`;

const TemplateNameError = styled.small`
  color: var(--destructive);
`;

const FieldName = styled.span<{ type: 'basic' | 'karaoke' }>`
  color: ${props =>
    props.type === 'basic' ? 'var(--text-muted)' : 'var(--border)'};
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 500;
`;
